// Course structure for The Decrypted English Method
// Complete Gamma embed configuration

export const courseInfo = {
    title: "The Decrypted English Method",
    subtitle: "An Alchemical Approach to Language Mastery",
    description: "Unveil the hidden structure of English through the ancient art of linguistic alchemy. Transform your understanding from memorization to true comprehension.",
    appLink: "https://etymwords.emergent.host"
};

export const sections = [
    {
        id: "section-1",
        number: "I",
        title: "The Opening of the Codex",
        subtitle: "A Compreensão do Método",
        description: "O véu é erguido e a mecânica invisível das palavras é revelada. Você recebe as chaves da decifração — não para decorar vocabulário, mas para ler a estrutura interna da língua com clareza e método.",
        icon: "book-open",
        color: "primary"
    },
    {
        id: "section-2",
        number: "II",
        title: "The Apprentice's Path of Decryption",
        subtitle: "A Jornada do Aprendiz Decifrador",
        description: "Raízes, prefixos, sufixos e composições são estudados como forças combináveis. Aqui você aprende a desmontar palavras, reconhecer padrões recorrentes e reconstruir significados com precisão lógica.",
        icon: "compass",
        color: "mystical"
    },
    {
        id: "section-3",
        number: "III",
        title: "The Crucibles of Mastery",
        subtitle: "Os Cadinhos de Prova e Consolidação",
        description: "O conhecimento passa pelo fogo do teste. O reconhecimento vira reflexo. A análise estrutural deixa de ser esforço e se torna habilidade ativa. Cada prova confirma o domínio conquistado.",
        icon: "flame",
        color: "accent"
    },
    {
        id: "section-4",
        number: "IV",
        title: "Applied Extractions",
        subtitle: "O Método em Ação",
        description: "Textos vivos, linguagem real, decifração prática. O método sai do laboratório e entra no mundo — revelando como extrair significado com velocidade, autonomia e autoridade interpretativa.",
        icon: "sparkles",
        color: "emerald"
    }
];

// Sub-modules for Section II - The Apprentice's Path
export const modules = {
    "section-2": [
        { id: "module-2-1", title: "I. The Root Code", description: "Understanding word origins" },
        { id: "module-2-2", title: "II. The Essence of the Cosmos: Nouns", description: "Naming the world" },
        { id: "module-2-3", title: "III. The Craft of Connection", description: "Prepositions and relationships" },
        { id: "module-2-4", title: "IV. The Suffix Cipher", description: "Word endings decoded" },
        { id: "module-2-5", title: "V. Prefix Transmutation", description: "Word beginnings transformed" },
        { id: "module-2-6", title: "VI. Casting Compound Spells", description: "Complex word formation" }
    ],
    "section-3": [
        { id: "module-3-1", title: "I. Crucible of Foundation: Roots", description: "Testing root knowledge" },
        { id: "module-3-2", title: "II. Crucible of Formation: Nouns", description: "Testing noun mastery" },
        { id: "module-3-3", title: "III. The Crucible of Actions: Verbs", description: "Testing verb mastery" },
        { id: "module-3-4", title: "IV. The Crucible of Qualities: Adjectives", description: "Testing adjective mastery" },
        { id: "module-3-5", title: "V. The Crucible of Precision: Adverbs", description: "Testing adverb mastery" },
        { id: "module-3-6", title: "VI. The Crucible of Significance: Prefixes", description: "Testing prefix mastery" },
        { id: "module-3-7", title: "VII. The Crucible of Termination: Suffixes", description: "Testing suffix mastery" }
    ]
};

// Complete lesson data with all Gamma embeds
export const lessons = [
    // ==========================================
    // SECTION I - THE OPENING OF THE CODEX
    // ==========================================
    {
        id: "lesson-1-1",
        sectionId: "section-1",
        moduleId: null,
        title: "Initiation to the Code",
        subtitle: "Your first step into the codex",
        embedUrl: "https://gamma.app/embed/nw88sp269pxfy1g",
        order: 1
    },
    {
        id: "lesson-1-2",
        sectionId: "section-1",
        moduleId: null,
        title: "Cracking the Code: Word Structure",
        subtitle: "Understanding how words are built",
        embedUrl: "https://gamma.app/embed/k165m84n4ivgakq",
        order: 2
    },
    {
        id: "lesson-1-3",
        sectionId: "section-1",
        moduleId: null,
        title: "Bridging the Code",
        subtitle: "Where Roots Begin to Transform",
        embedUrl: "https://gamma.app/embed/y85iyhdajwwqi06",
        order: 3
    },

    // ==========================================
    // SECTION II - THE APPRENTICE'S PATH
    // ==========================================
    
    // Module I: The Root Code
    {
        id: "lesson-2-1-1",
        sectionId: "section-2",
        moduleId: "module-2-1",
        title: "Origins",
        subtitle: "The Root Code - Part 1",
        embedUrl: "https://gamma.app/embed/kj27sq2kpr4rcyy",
        order: 1
    },
    {
        id: "lesson-2-1-2",
        sectionId: "section-2",
        moduleId: "module-2-1",
        title: "Embedded Power",
        subtitle: "The Root Code - Part 2",
        embedUrl: "https://gamma.app/embed/8um46kgbb8oavss",
        order: 2
    },
    {
        id: "lesson-2-1-3",
        sectionId: "section-2",
        moduleId: "module-2-1",
        title: "Unmasking Patterns",
        subtitle: "The Root Code - Part 3",
        embedUrl: "https://gamma.app/embed/zahip1cb3ewyuxe",
        order: 3
    },
    {
        id: "lesson-2-1-4",
        sectionId: "section-2",
        moduleId: "module-2-1",
        title: "Revealing the Core",
        subtitle: "The Root Code - Part 4",
        embedUrl: "https://gamma.app/embed/fcpzf0ij7xvg8u7",
        order: 4
    },

    // Module II: The Essence of the Cosmos: Nouns
    {
        id: "lesson-2-2-1",
        sectionId: "section-2",
        moduleId: "module-2-2",
        title: "Naming the Nebulae",
        subtitle: "The Essence of the Cosmos - Part 1",
        embedUrl: "https://gamma.app/embed/ojipchlve5k5tlo",
        order: 1
    },
    {
        id: "lesson-2-2-2",
        sectionId: "section-2",
        moduleId: "module-2-2",
        title: "Shaping the Substance",
        subtitle: "The Essence of the Cosmos - Part 2",
        embedUrl: "https://gamma.app/embed/sclng80nx7n7p2f",
        order: 2
    },
    {
        id: "lesson-2-2-3",
        sectionId: "section-2",
        moduleId: "module-2-2",
        title: "Forging the Forms",
        subtitle: "The Essence of the Cosmos - Part 3",
        embedUrl: "https://gamma.app/embed/tss4ce3l75pgsch",
        order: 3
    },
    {
        id: "lesson-2-2-4",
        sectionId: "section-2",
        moduleId: "module-2-2",
        title: "Emerging from the Ether",
        subtitle: "The Essence of the Cosmos - Part 4",
        embedUrl: "https://gamma.app/embed/ekonolblup8gtc4",
        order: 4
    },
    {
        id: "lesson-2-2-5",
        sectionId: "section-2",
        moduleId: "module-2-2",
        title: "Materializing Matter",
        subtitle: "The Essence of the Cosmos - Part 5",
        embedUrl: "https://gamma.app/embed/dhjvyw3dq598er3",
        order: 5
    },

    // Module III: The Craft of Connection
    {
        id: "lesson-2-3-1",
        sectionId: "section-2",
        moduleId: "module-2-3",
        title: "Prepositions: Between and Beyond",
        subtitle: "The Craft of Connection",
        embedUrl: "https://gamma.app/embed/cncuc151okq4lqr",
        order: 1
    },

    // Module IV: The Suffix Cipher
    {
        id: "lesson-2-4-1",
        sectionId: "section-2",
        moduleId: "module-2-4",
        title: "Nouns - The Essence of Names",
        subtitle: "The Suffix Cipher - Part 1",
        embedUrl: "https://gamma.app/embed/6edww0pmw4ncsh7",
        order: 1
    },
    {
        id: "lesson-2-4-2",
        sectionId: "section-2",
        moduleId: "module-2-4",
        title: "Verbs - The Forge of Action",
        subtitle: "The Suffix Cipher - Part 2",
        embedUrl: "https://gamma.app/embed/hxgbcd1d9373peb",
        order: 2
    },
    {
        id: "lesson-2-4-3",
        sectionId: "section-2",
        moduleId: "module-2-4",
        title: "Conjugations - Getting In Tense",
        subtitle: "The Suffix Cipher - Part 3",
        embedUrl: "https://gamma.app/embed/2l044hbpsz7co0w",
        order: 3
    },
    {
        id: "lesson-2-4-4",
        sectionId: "section-2",
        moduleId: "module-2-4",
        title: "Adjectives - The Cloaks of Quality",
        subtitle: "The Suffix Cipher - Part 4",
        embedUrl: "https://gamma.app/embed/3ex6dmz79oq48vq",
        order: 4
    },
    {
        id: "lesson-2-4-5",
        sectionId: "section-2",
        moduleId: "module-2-4",
        title: "Adverbs - The Colors of Motion",
        subtitle: "The Suffix Cipher - Part 5",
        embedUrl: "https://gamma.app/embed/7qf61uu7c0vghu3",
        order: 5
    },

    // Module V: Prefix Transmutation
    {
        id: "lesson-2-5-1",
        sectionId: "section-2",
        moduleId: "module-2-5",
        title: "Where Words Begin to Move",
        subtitle: "Prefix Transmutation - Part 1",
        embedUrl: "https://gamma.app/embed/zez1f8ux7uk65tf",
        order: 1
    },
    {
        id: "lesson-2-5-2",
        sectionId: "section-2",
        moduleId: "module-2-5",
        title: "The Code of Quantity",
        subtitle: "Prefix Transmutation - Part 2",
        embedUrl: "https://gamma.app/embed/6gyotgipjtmum2c",
        order: 2
    },
    {
        id: "lesson-2-5-3",
        sectionId: "section-2",
        moduleId: "module-2-5",
        title: "The Law of Opposition",
        subtitle: "Prefix Transmutation - Part 3",
        embedUrl: "https://gamma.app/embed/z5azvn92omlf22b",
        order: 3
    },
    {
        id: "lesson-2-5-4",
        sectionId: "section-2",
        moduleId: "module-2-5",
        title: "The Chasm of Catalysts",
        subtitle: "Prefix Transmutation - Part 4",
        embedUrl: "https://gamma.app/embed/gfs2he5cj9rtdub",
        order: 4
    },
    {
        id: "lesson-2-5-5",
        sectionId: "section-2",
        moduleId: "module-2-5",
        title: "The Veil of Description",
        subtitle: "Prefix Transmutation - Part 5",
        embedUrl: "https://gamma.app/embed/wnmzi3xdd05tj7f",
        order: 5
    },

    // Module VI: Casting Compound Spells
    {
        id: "lesson-2-6-1",
        sectionId: "section-2",
        moduleId: "module-2-6",
        title: "Primordial Principality",
        subtitle: "Casting Compound Spells - Part 1",
        embedUrl: "https://gamma.app/embed/cx63mmeqt3e1k6a",
        order: 1
    },
    {
        id: "lesson-2-6-2",
        sectionId: "section-2",
        moduleId: "module-2-6",
        title: "Institutionalization of the Idea",
        subtitle: "Casting Compound Spells - Part 2",
        embedUrl: "https://gamma.app/embed/9imgmv1utvbwgzm",
        order: 2
    },
    {
        id: "lesson-2-6-3",
        sectionId: "section-2",
        moduleId: "module-2-6",
        title: "Legitimization of the Lessons",
        subtitle: "Casting Compound Spells - Part 3",
        embedUrl: "https://gamma.app/embed/lcnjm3h25zgi29y",
        order: 3
    },
    {
        id: "lesson-2-6-4",
        sectionId: "section-2",
        moduleId: "module-2-6",
        title: "Epistemological Epiphany",
        subtitle: "Casting Compound Spells - Part 4",
        embedUrl: "https://gamma.app/embed/ejnpdouo9zpqp0t",
        order: 4
    },

    // ==========================================
    // SECTION III - THE CRUCIBLES
    // ==========================================

    // Module I: Crucible of Foundation: Roots
    {
        id: "lesson-3-1-1",
        sectionId: "section-3",
        moduleId: "module-3-1",
        title: "The Trial of Origins",
        subtitle: "Crucible of Foundation - Part 1",
        embedUrl: "https://gamma.app/embed/do5uu8zr1c4w5sw",
        order: 1
    },
    {
        id: "lesson-3-1-2",
        sectionId: "section-3",
        moduleId: "module-3-1",
        title: "The Measure of Source",
        subtitle: "Crucible of Foundation - Part 2",
        embedUrl: "https://gamma.app/embed/1p59orbn63fdlx6",
        order: 2
    },
    {
        id: "lesson-3-1-3",
        sectionId: "section-3",
        moduleId: "module-3-1",
        title: "Scales of the Core",
        subtitle: "Crucible of Foundation - Part 3",
        embedUrl: "https://gamma.app/embed/yccox0uhhi9ald9",
        order: 3
    },
    {
        id: "lesson-3-1-4",
        sectionId: "section-3",
        moduleId: "module-3-1",
        title: "Rite of Recognition",
        subtitle: "Crucible of Foundation - Part 4",
        embedUrl: "https://gamma.app/embed/fymm9t5462j3n04",
        order: 4
    },

    // Module II: Crucible of Formation: Nouns
    {
        id: "lesson-3-2-1",
        sectionId: "section-3",
        moduleId: "module-3-2",
        title: "Rite of All Things",
        subtitle: "Crucible of Formation - Part 1",
        embedUrl: "https://gamma.app/embed/o6c1wg463huvviv",
        order: 1
    },
    {
        id: "lesson-3-2-2",
        sectionId: "section-3",
        moduleId: "module-3-2",
        title: "The Naming Sigil",
        subtitle: "Crucible of Formation - Part 2",
        embedUrl: "https://gamma.app/embed/xb8wv0bl478vfao",
        order: 2
    },
    {
        id: "lesson-3-2-3",
        sectionId: "section-3",
        moduleId: "module-3-2",
        title: "The Word Bound Axis",
        subtitle: "Crucible of Formation - Part 3",
        embedUrl: "https://gamma.app/embed/qtkb9zfi09zjxxa",
        order: 3
    },
    {
        id: "lesson-3-2-4",
        sectionId: "section-3",
        moduleId: "module-3-2",
        title: "Lexical Conquest",
        subtitle: "Crucible of Formation - Part 4",
        embedUrl: "https://gamma.app/embed/0lfwrxfa9yjnwe0",
        order: 4
    },

    // Module III: The Crucible of Actions: Verbs
    {
        id: "lesson-3-3-1",
        sectionId: "section-3",
        moduleId: "module-3-3",
        title: "The Hand and The Word",
        subtitle: "Crucible of Actions - Part 1",
        embedUrl: "https://gamma.app/embed/obgzl7sgo5ob40m",
        order: 1
    },
    {
        id: "lesson-3-3-2",
        sectionId: "section-3",
        moduleId: "module-3-3",
        title: "The Spoken Deed",
        subtitle: "Crucible of Actions - Part 2",
        embedUrl: "https://gamma.app/embed/kx5jb9y5nyq57wj",
        order: 2
    },
    {
        id: "lesson-3-3-3",
        sectionId: "section-3",
        moduleId: "module-3-3",
        title: "Ritual of Time",
        subtitle: "Crucible of Actions - Part 3",
        embedUrl: "https://gamma.app/embed/qn17i0qsrcf5zfu",
        order: 3
    },
    {
        id: "lesson-3-3-4",
        sectionId: "section-3",
        moduleId: "module-3-3",
        title: "The Words in Motion",
        subtitle: "Crucible of Actions - Part 4",
        embedUrl: "https://gamma.app/embed/q86i7uxwac75s92",
        order: 4
    },

    // Module IV: The Crucible of Qualities: Adjectives
    {
        id: "lesson-3-4-1",
        sectionId: "section-3",
        moduleId: "module-3-4",
        title: "The Measure of Essence",
        subtitle: "Crucible of Qualities - Part 1",
        embedUrl: "https://gamma.app/embed/ihzryj0utoapyp3",
        order: 1
    },
    {
        id: "lesson-3-4-2",
        sectionId: "section-3",
        moduleId: "module-3-4",
        title: "The Tone of Reality",
        subtitle: "Crucible of Qualities - Part 2",
        embedUrl: "https://gamma.app/embed/qzzg6w5wb5h55aw",
        order: 2
    },
    {
        id: "lesson-3-4-3",
        sectionId: "section-3",
        moduleId: "module-3-4",
        title: "The Temper of Things",
        subtitle: "Crucible of Qualities - Part 3",
        embedUrl: "https://gamma.app/embed/oo3xwh04kq57p60",
        order: 3
    },
    {
        id: "lesson-3-4-4",
        sectionId: "section-3",
        moduleId: "module-3-4",
        title: "The Qualia Key",
        subtitle: "Crucible of Qualities - Part 4",
        embedUrl: "https://gamma.app/embed/spbjdw4vfi6if08",
        order: 4
    },
    {
        id: "lesson-3-4-5",
        sectionId: "section-3",
        moduleId: "module-3-4",
        title: "The Qualitative Seal",
        subtitle: "Crucible of Qualities - Part 5",
        embedUrl: "https://gamma.app/embed/im4cdj5gtz1b5ry",
        order: 5
    },

    // Module V: The Crucible of Precision: Adverbs
    {
        id: "lesson-3-5-1",
        sectionId: "section-3",
        moduleId: "module-3-5",
        title: "The Way of Motion",
        subtitle: "Crucible of Precision - Part 1",
        embedUrl: "https://gamma.app/embed/g0ur7c9n2xrwkot",
        order: 1
    },
    {
        id: "lesson-3-5-2",
        sectionId: "section-3",
        moduleId: "module-3-5",
        title: "The Hidden Manner",
        subtitle: "Crucible of Precision - Part 2",
        embedUrl: "https://gamma.app/embed/z4uuo9iojz5unni",
        order: 2
    },
    {
        id: "lesson-3-5-3",
        sectionId: "section-3",
        moduleId: "module-3-5",
        title: "The Shade of Action",
        subtitle: "Crucible of Precision - Part 3",
        embedUrl: "https://gamma.app/embed/05be089g3qofhir",
        order: 3
    },
    {
        id: "lesson-3-5-4",
        sectionId: "section-3",
        moduleId: "module-3-5",
        title: "The Flow of Intention",
        subtitle: "Crucible of Precision - Part 4",
        embedUrl: "https://gamma.app/embed/f61lgcvefh2ym8h",
        order: 4
    },

    // Module VI: The Crucible of Significance: Prefixes
    {
        id: "lesson-3-6-1",
        sectionId: "section-3",
        moduleId: "module-3-6",
        title: "The Tempering of Alteration",
        subtitle: "Crucible of Significance - Part 1",
        embedUrl: "https://gamma.app/embed/yyuavsqb9ep3qww",
        order: 1
    },
    {
        id: "lesson-3-6-2",
        sectionId: "section-3",
        moduleId: "module-3-6",
        title: "Test of Transmutation",
        subtitle: "Crucible of Significance - Part 2",
        embedUrl: "https://gamma.app/embed/umzgpw0t1gdq2k8",
        order: 2
    },
    {
        id: "lesson-3-6-3",
        sectionId: "section-3",
        moduleId: "module-3-6",
        title: "Testimony of Proficiency",
        subtitle: "Crucible of Significance - Part 3",
        embedUrl: "https://gamma.app/embed/87u0ha08i30du0w",
        order: 3
    },

    // Module VII: The Crucible of Termination: Suffixes
    {
        id: "lesson-3-7-1",
        sectionId: "section-3",
        moduleId: "module-3-7",
        title: "Function of the Form",
        subtitle: "Crucible of Termination - Part 1",
        embedUrl: "https://gamma.app/embed/wvan2xz6itsvnir",
        order: 1
    },
    {
        id: "lesson-3-7-2",
        sectionId: "section-3",
        moduleId: "module-3-7",
        title: "Fulfilling the Fate",
        subtitle: "Crucible of Termination - Part 2",
        embedUrl: "https://gamma.app/embed/t0u4k4ydc8zv50j",
        order: 2
    },
    {
        id: "lesson-3-7-3",
        sectionId: "section-3",
        moduleId: "module-3-7",
        title: "Transforming the Material",
        subtitle: "Crucible of Termination - Part 3",
        embedUrl: "https://gamma.app/embed/vbq8xra6rawfcq1",
        order: 3
    },
    {
        id: "lesson-3-7-4",
        sectionId: "section-3",
        moduleId: "module-3-7",
        title: "The Meaning is Sealed",
        subtitle: "Crucible of Termination - Part 4",
        embedUrl: "https://gamma.app/embed/jcaugx1fbcf3vh0",
        order: 4
    },

    // ==========================================
    // SECTION IV - APPLIED EXTRACTIONS
    // ==========================================
    {
        id: "lesson-4-1",
        sectionId: "section-4",
        moduleId: null,
        title: "The Five Degrees",
        subtitle: "Applied Extractions - Part 1",
        embedUrl: "https://gamma.app/embed/yi7w5obwqgn1231",
        order: 1
    },
    {
        id: "lesson-4-2",
        sectionId: "section-4",
        moduleId: null,
        title: "Lingual Alchemy",
        subtitle: "Applied Extractions - Part 2",
        embedUrl: "https://gamma.app/embed/4onglnrlwhckys3",
        order: 2
    },
    {
        id: "lesson-4-3",
        sectionId: "section-4",
        moduleId: null,
        title: "The Genesis",
        subtitle: "Applied Extractions - Part 3",
        embedUrl: "https://gamma.app/embed/9md9urslb0baayz",
        order: 3
    },
    {
        id: "lesson-4-4",
        sectionId: "section-4",
        moduleId: null,
        title: "Prima Materia",
        subtitle: "Applied Extractions - Part 4",
        embedUrl: "https://gamma.app/embed/jetfvebxfukvf8g",
        order: 4
    },
    {
        id: "lesson-4-5",
        sectionId: "section-4",
        moduleId: null,
        title: "The Apprentice",
        subtitle: "Applied Extractions - Part 5",
        embedUrl: "https://gamma.app/embed/vncl396ao9jmop2",
        order: 5
    },
    {
        id: "lesson-4-6",
        sectionId: "section-4",
        moduleId: null,
        title: "The Rat",
        subtitle: "Applied Extractions - Part 6",
        embedUrl: "https://gamma.app/embed/q1jef1jki6ft291",
        order: 6
    },
    {
        id: "lesson-4-7",
        sectionId: "section-4",
        moduleId: null,
        title: "The Automaton",
        subtitle: "Applied Extractions - Part 7",
        embedUrl: "https://gamma.app/embed/yggyynxsfhcvy05",
        order: 7
    },
    {
        id: "lesson-4-8",
        sectionId: "section-4",
        moduleId: null,
        title: "The Library",
        subtitle: "Applied Extractions - Part 8",
        embedUrl: "https://gamma.app/embed/1l5qv0ns3kpqyjy",
        order: 8
    },
    {
        id: "lesson-4-9",
        sectionId: "section-4",
        moduleId: null,
        title: "The Mirror Chamber",
        subtitle: "Applied Extractions - Part 9",
        embedUrl: "https://gamma.app/embed/hh964oo5fhkt259",
        order: 9
    },
    {
        id: "lesson-4-10",
        sectionId: "section-4",
        moduleId: null,
        title: "The Experiment",
        subtitle: "Applied Extractions - Part 10",
        embedUrl: "https://gamma.app/embed/fk0spi8f8dm238r",
        order: 10
    },
    {
        id: "lesson-4-11",
        sectionId: "section-4",
        moduleId: null,
        title: "The Silent Visitor",
        subtitle: "Applied Extractions - Part 11",
        embedUrl: "https://gamma.app/embed/vligns9q0hxglnv",
        order: 11
    },
    {
        id: "lesson-4-12",
        sectionId: "section-4",
        moduleId: null,
        title: "The Festival of Shadows",
        subtitle: "Applied Extractions - Part 12",
        embedUrl: "https://gamma.app/embed/5oxtf1uzajnvxyj",
        order: 12
    },
    {
        id: "lesson-4-final",
        sectionId: "section-4",
        moduleId: null,
        title: "The Philosopher's Seal",
        subtitle: "Course Completion",
        embedUrl: "https://gamma.app/embed/7n69oo8qczc6prk",
        order: 13,
        isFinal: true
    }
];

// Helper functions
export const getLessonsBySection = (sectionId) => {
    return lessons
        .filter(lesson => lesson.sectionId === sectionId)
        .sort((a, b) => a.order - b.order);
};

export const getLessonsByModule = (moduleId) => {
    return lessons
        .filter(lesson => lesson.moduleId === moduleId)
        .sort((a, b) => a.order - b.order);
};

export const getModulesBySection = (sectionId) => {
    return modules[sectionId] || [];
};

export const getLessonById = (lessonId) => {
    return lessons.find(lesson => lesson.id === lessonId);
};

export const getSectionById = (sectionId) => {
    return sections.find(section => section.id === sectionId);
};

export const getModuleById = (moduleId) => {
    for (const sectionId in modules) {
        const module = modules[sectionId].find(m => m.id === moduleId);
        if (module) return module;
    }
    return null;
};

export const getNextLesson = (currentLessonId) => {
    const currentIndex = lessons.findIndex(l => l.id === currentLessonId);
    if (currentIndex >= 0 && currentIndex < lessons.length - 1) {
        return lessons[currentIndex + 1];
    }
    return null;
};

export const getPreviousLesson = (currentLessonId) => {
    const currentIndex = lessons.findIndex(l => l.id === currentLessonId);
    if (currentIndex > 0) {
        return lessons[currentIndex - 1];
    }
    return null;
};

export const getAdjacentLessonsInSection = (lessonId) => {
    const lesson = getLessonById(lessonId);
    if (!lesson) return { prev: null, next: null };
    
    const sectionLessons = getLessonsBySection(lesson.sectionId);
    const currentIndex = sectionLessons.findIndex(l => l.id === lessonId);
    
    return {
        prev: currentIndex > 0 ? sectionLessons[currentIndex - 1] : null,
        next: currentIndex < sectionLessons.length - 1 ? sectionLessons[currentIndex + 1] : null
    };
};

export const getAdjacentLessonsInModule = (lessonId) => {
    const lesson = getLessonById(lessonId);
    if (!lesson || !lesson.moduleId) return { prev: null, next: null };
    
    const moduleLessons = getLessonsByModule(lesson.moduleId);
    const currentIndex = moduleLessons.findIndex(l => l.id === lessonId);
    
    return {
        prev: currentIndex > 0 ? moduleLessons[currentIndex - 1] : null,
        next: currentIndex < moduleLessons.length - 1 ? moduleLessons[currentIndex + 1] : null
    };
};

// Get total lesson count
export const getTotalLessonCount = () => lessons.length;

// Get lessons count by section
export const getLessonCountBySection = (sectionId) => {
    return lessons.filter(l => l.sectionId === sectionId).length;
};
